import { useCallback, useContext, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { useAppLoading } from "../../components/layout/app-loading-provider";
import { CognitoContext } from "../../contexts/cognito/cognito-provider";
import { getVoucherError, isInvalidVoucher } from "../../library/core/messages";
import { CoreServiceSelector } from "../../services/core/service-selector";
import { BarcodeInput } from "../common/barcode-scanner/barcode-input";
import { CameraLoader } from "../common/barcode-scanner/camera-loader";
import { GetEventInputValue, ValidateFormView } from "../form/core";
import FixedBottomButton from "../form/FixedBottomButton";
import { InputNumber } from "../form/input-number";
import { InvalidInputMessage } from "../form/invalid-input";
import { PrimaryButton } from "../form/primary-button";

const Form = styled.form`
  min-height: 300px;
`;

const InputWithButtonDiv = styled.div``;

const voucherField = {
  name: "barcode",
  label: "Enter or scan the card number",
  hint: "Manually enter number",
  type: "text",
  required: true,
  isRequiredMessage: "Card/certificate number can't be empty",
  validate: (value, dataModel) => {
    if (!value || value.length < 10)
      return `${value} is an invalid card number`;
    return null;
  },
};

export const PullVoucherType = ({
  dataModel,
  buttonLabel,
  serviceDispatcher,
  onSetVoucher,
  otherPanels,
  otherButtons,
  enableBarcodeScanning,
}) => {
  dataModel ||= {};
  const { state, dispatch } = useContext(CognitoContext);
  const { showLoading, hideLoading } = useAppLoading();

  const voucherNumberRef = useRef();
  const [model, setModel] = useState({
    barcode: "",
  });
  const [formState, setFormState] = useState({});
  const [showBarcodeInput, setShowBarcodeInput] = useState(false);
  const [barcodeNotSupported, setBarcodeNotSupported] = useState(false);
  const [cameraOptions, setCameraOptions] = useState([]);
  const submitBtnRef = useRef();
  useEffect(() => {
    if (submitBtnRef?.current) {
    }
  }, []);

  const [errors, setErrors] = useState({});
  buttonLabel ||= "Next";
  const onChangeHandler = (event) => {
    console.log("onChangeHandler", event.target.name, event.target.value);
    setModel({
      ...model,
      [event.target.name]: GetEventInputValue(event, true),
    });
  };

  const findVoucher = useCallback(
    async (barcode = null) => {
      let service = null;
      showLoading();
      if (serviceDispatcher && typeof serviceDispatcher === "function")
        service = await serviceDispatcher();
      else service = CoreServiceSelector.GetRedemptionService(state.session);

      setFormState((prev) => ({
        ...prev,
        isProcessing: true,
      }));
      const code = barcode !== null ? barcode : model.barcode;
      service
        .getVoucherInfo(code, dataModel.companyId)
        .then((data) => {
          setFormState((prev) => ({
            ...prev,
            isProcessing: false,
          }));

          if (isInvalidVoucher(data)) {
            hideLoading();
            let error = getVoucherError(data);
            setErrors({
              barcode: error,
            });
            setModel((prev) => ({
              ...prev,
              barcode: null,
            }));
            return;
          }
          hideLoading();
          onSetVoucher && onSetVoucher(data);
        })
        .catch((err) => {
          hideLoading();
          setFormState((prev) => ({
            ...prev,
            isProcessing: false,
          }));

          setErrors({
            barcode: "Something wrong happened, please try later",
          });
        });
    },
    [
      showLoading,
      serviceDispatcher,
      state.session,
      model.barcode,
      dataModel.companyId,
      hideLoading,
      onSetVoucher,
    ]
  );

  const onSubmit = useCallback(
    (event) => {
      event?.preventDefault();
      const errs = ValidateFormView({ fields: [voucherField], model });
      setErrors(errs);

      if (!errs || Object.keys(errs).length === 0) {
        findVoucher();
      } else {
        setModel((prev) => ({
          ...prev,
          barcode: null,
        }));
      }
    },
    [model, findVoucher]
  );
  const onSaveBarcode = useCallback(
    (barcode) => {
      setModel((prev) => ({
        ...prev,
        barcode,
      }));
      findVoucher(barcode);
    },
    [findVoucher]
  );
  const errorMessage = errors && errors.barcode;
  const value = model.barcode || "";
  const hintId = `${voucherField.name}Hint`;

  const onLoadCameraOptions = (options) => {
    console.log("camera options", options);
    setCameraOptions(options);
  };

  const onHideBarcodeInput = (isTimeOut) => {
    console.log("onHideBarcodeInput");
    setShowBarcodeInput(false);
    if (isTimeOut) {
      console.log("Its indeed a timeout");
      setModel({
        ...model,
        barcode: null,
      });
    }
  };

  const onTimeOutHandler = () => {
    console.log("Its indeed a timeout");
    setShowBarcodeInput(false);
  };

  return (
    <div>
      <CameraLoader
        load={true}
        onLoaded={onLoadCameraOptions}
        onFeatureNotSupported={() => setBarcodeNotSupported(true)}
      />

      {!barcodeNotSupported && (
        <BarcodeInput
          show={showBarcodeInput}
          cameraOptions={cameraOptions}
          onDismiss={onHideBarcodeInput}
          onNotSupported={() => setBarcodeNotSupported(true)}
          onScanComplete={(barcode) => {
            onSaveBarcode(barcode);
            setShowBarcodeInput(false);
          }}
          onTimeOut={onTimeOutHandler}
        />
      )}

      <Form>
        <div>
          <div className="mb-3 w-full max-w-lg mx-auto items-center ">
            <label
              className="text-center w-full font-headings-primary font-medium text-[28px] leading-[33.6px] mb-2 mt-[30vh]"
              htmlFor={voucherField.name}
            >
              {voucherField.label}
            </label>
            <div>
              <p className="text-center mb-4 text-[16px] font-body-primary"></p>
            </div>
            <InputNumber
              ref={voucherNumberRef}
              required={false}
              type="text"
              inputMode="numeric"
              className={
                "mt-2 md:max-w-[500px] mx-auto" +
                (errorMessage ? " is-invalid " : "")
              }
              name={voucherField.name}
              id={voucherField.name}
              label={voucherField.label}
              aria-describedby={hintId}
              value={value}
              autoComplete="off"
              placeholder={voucherField.hint}
              onChange={onChangeHandler}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === "Go" || e.key === "Done") {
                  onSubmit(e);
                }
              }}
              onInput={(e) => {
                if (e.inputType === "insertLineBreak") {
                  onSubmit(e);
                }
              }}
            />
            <InvalidInputMessage errorMessage={errorMessage} />
            <FixedBottomButton>
              {enableBarcodeScanning &&
                !model.barcode &&
                !barcodeNotSupported && (
                  <PrimaryButton
                    type="button"
                    onClick={() => {
                      setShowBarcodeInput(true);
                    }}
                    className="mt-4 md:max-w-[500px] w-full mx-auto"
                  >
                    <span>
                      <i className="bi bi-qr-code-scan"></i>{" "}
                      <span>Scan a code</span>
                    </span>
                  </PrimaryButton>
                )}
              {model.barcode && (
                <PrimaryButton
                  disabled={!model.barcode}
                  ref={submitBtnRef}
                  type="submit"
                  className="mt-4 md:max-w-[500px] mx-auto"
                  onClick={onSubmit}
                >
                  {buttonLabel}
                </PrimaryButton>
              )}
            </FixedBottomButton>
            {/* <ToggleView show={voucherField.hint}>
                                <div id={hintId} className="form-text">{voucherField.hint}</div>
                            </ToggleView> */}
          </div>
        </div>

        <div>{otherPanels}</div>

        {/* <ButtonsPanel>
                        {otherButtons}
                    </ButtonsPanel> */}
      </Form>
    </div>
  );
};
